/* eslint-disable react-hooks/exhaustive-deps */
// @vendors
import { type ChangeEvent, type FormEvent, useEffect, useState } from 'react';
import toast from 'react-hot-toast';

// @services
import CityService, { City } from '../../services/city.service';

// @interfaces
import { IModalRequestPromotionProps } from '../../domain/interfaces/Promotions';
import XervixLoader from '../ui/Xervix-loader';
import { useTheme } from '../../context/ThemeContext';

const ModalRequestPromotion = ({
    isOpen,
    onClose,
    onSubmit,
    phone,
    serviceLocation,
    setPhone,
    setServiceLocation,
    isLoading
}: IModalRequestPromotionProps) => {
    const { theme } = useTheme();
    const [touched, setTouched] = useState(false);
    const [cities, setCities] = useState<City[]>([]);

    const fetchCities = async () => {
        try {
            const cities = await CityService.getAllCities();
            setCities(cities);
        } catch (error) {
            toast.error('Error al cargar las ciudades');
        }
    };

    useEffect(() => {
        if (isOpen) fetchCities();
    }, [isOpen]);

    if (!isOpen) return null;

    const handlePhoneChange = (event: ChangeEvent<HTMLInputElement>) => {
        const onlyDigits = event.target.value.replace(/\D/g, '').slice(0, 10);
        setPhone(onlyDigits);
    };

    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setTouched(true);
        if (phone.trim().length !== 10 || !serviceLocation.trim()) return;

        onSubmit?.({
            phone: phone.trim(),
            serviceLocation: serviceLocation.trim(),
        });
        setTouched(false);
    };

    const handleClose = () => {
        setPhone('');
        setServiceLocation('');
        setTouched(false);
        onClose();
    };

    const phoneInvalid = touched && phone.trim().length !== 10;
    const serviceLocationInvalid = touched && !serviceLocation.trim();

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            aria-modal="true"
            role="dialog"
        >
            {isLoading ? (
                <div className="flex flex-col items-center justify-center py-4">
                    <div className="w-full flex justify-center items-center">
                        <XervixLoader />
                    </div>
                    <p className={`mt-4 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                        Procesando tu solicitud...
                    </p>
                </div>
            ) : (
                <div
                    className="w-full max-w-lg rounded-xl bg-white shadow-2xl outline-none dark:bg-gray-900 dark:border dark:border-gray-700"
                    onClick={(event) => event.stopPropagation()}
                >
                    <header className="flex flex-col items-start justify-between gap-2 border-b border-gray-200 px-6 py-4 dark:border-gray-700">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                            Solicitar promoción
                        </h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                            Por favor, ingresa los siguientes datos para solicitar la promoción.
                        </p>
                    </header>

                    <form onSubmit={handleSubmit} className="px-6 py-5">
                        <div className="space-y-4">
                            <div>
                                <label
                                    htmlFor="promotion-phone"
                                    className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
                                >
                                    Teléfono
                                </label>
                                <input
                                    id="promotion-phone"
                                    name="phone"
                                    type="tel"
                                    inputMode="numeric"
                                    pattern="[0-9]{10}"
                                    maxLength={10}
                                    value={phone}
                                    onChange={handlePhoneChange}
                                    className={`w-full rounded-lg border px-3 py-2 text-sm shadow-sm focus:border-xervix-primary focus:outline-none focus:ring-2 focus:ring-xervix-primary/30 dark:bg-gray-800 dark:text-gray-100 ${phoneInvalid
                                        ? 'border-red-500 focus:border-blue-500 focus:ring-blue-200'
                                        : 'border-gray-300 dark:border-gray-700'
                                        }`}
                                    placeholder="Ingresa número telefónico"
                                    required
                                />
                                {phone.trim().length > 0 && phone.trim().length < 10 && <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                    Ingresa un número de 10 dígitos.
                                </p>}
                                {phoneInvalid && (
                                    <p className="mt-1 text-sm text-red-600">
                                        El número debe tener 10 dígitos.
                                    </p>
                                )}
                            </div>

                            <div>
                                <label
                                    htmlFor="promotion-service-location"
                                    className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
                                >
                                    Lugar del servicio
                                </label>
                                <select
                                    id="promotion-service-location"
                                    name="serviceLocation"
                                    value={serviceLocation}
                                    onChange={(event) => setServiceLocation(event.target.value)}
                                    className={`w-full rounded-lg border px-3 py-2 text-sm shadow-sm focus:border-xervix-primary focus:outline-none focus:ring-2 focus:ring-xervix-primary/30 dark:bg-gray-800 dark:text-gray-100 ${serviceLocationInvalid
                                        ? 'border-red-500 focus:border-blue-500 focus:ring-blue-200'
                                        : 'border-gray-300 dark:border-gray-700'
                                        }`}
                                    required
                                >
                                    <option value="" disabled>
                                        Selecciona un lugar
                                    </option>
                                    {cities?.map((city) => (
                                        <option key={city?.id} value={city?.name}>
                                            {city?.name}
                                        </option>
                                    ))}
                                </select>
                                {serviceLocationInvalid && (
                                    <p className="mt-1 text-sm text-red-600">
                                        Este campo es obligatorio.
                                    </p>
                                )}
                            </div>
                        </div>

                        <div className="mt-6 flex justify-end gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
                            <button
                                type="button"
                                onClick={handleClose}
                                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-xervix-primary/40 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800"
                            >
                                Cancelar
                            </button>
                            <button
                                type="submit"
                                className="rounded-lg bg-xervix-primary px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-xervix-primary-dark focus:outline-none focus:ring-2 focus:ring-xervix-primary/60"
                            >
                                Solicitar
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
};

export default ModalRequestPromotion;